import React from 'react';
import { Box, useTheme } from '@mui/material';
import SummaryCard from './SummaryCard';

// Defines the structure of the data expected from the GraphQL query
interface OverviewData {
  totalTokens: { value: number; change: number | null; monthlyTrend: number[] };
  totalCost: { value: number; change: number | null; monthlyTrend: number[] };
  llmCalls: { value: number; change: number | null; monthlyTrend: number[] };
}

// Defines the component's props, accepting backend data and loading state
interface LlmOverviewCardsProps {
  isLoading?: boolean;
  overviewData?: OverviewData;
}

// Helper function to calculate and format the percentage change.
const formatPercentageChange = (current: number, change: number | null): string => {
    if (change === null || change === 0) {
        return '0.00%';
    }
    const previous = current - change;
    if (previous === 0) {
        return '100.00%';
    }
    const percentage = (change / Math.abs(previous)) * 100;
    return `${Math.abs(percentage).toFixed(2)}%`;
};

const LlmOverviewCards: React.FC<LlmOverviewCardsProps> = ({ isLoading, overviewData }) => {
    const theme = useTheme();
    const gap = theme.spacing(3);

    const cardItems = overviewData ? [
        {
            title: "Total Tokens",
            amount: overviewData.totalTokens.value.toLocaleString(),
            change: formatPercentageChange(overviewData.totalTokens.value, overviewData.totalTokens.change),
            changeType: (overviewData.totalTokens.change ?? 0) <= 0 ? "negative" as const : "positive" as const,
            changeDescription: `Previous month: ${(overviewData.totalTokens.value - (overviewData.totalTokens.change ?? 0)).toLocaleString()}`,
            textureImagePath: '/textures/llm-1.svg',
            chartData: overviewData.totalTokens.monthlyTrend.map(v => ({ value: v })),
            lastBarColor: (overviewData.totalTokens.change ?? 0) < 0 ? '#CA2B20' : '#03A65A',
        },
        {
            title: "Total Cost",
            amount: `$${overviewData.totalCost.value.toFixed(2)}`,
            change: formatPercentageChange(overviewData.totalCost.value, overviewData.totalCost.change),
            changeType: (overviewData.totalCost.change ?? 0) <= 0 ? "negative" as const : "positive" as const,
            changeDescription: `Previous month: $${(overviewData.totalCost.value - (overviewData.totalCost.change ?? 0)).toFixed(2)}`,
            textureImagePath: '/textures/llm-2.svg',
            chartData: overviewData.totalCost.monthlyTrend.map(v => ({ value: v })),
            lastBarColor: (overviewData.totalCost.change ?? 0) < 0 ? '#CA2B20' : '#03A65A',
        },
        {
            title: "LLM Calls",
            amount: overviewData.llmCalls.value.toLocaleString(),
            change: formatPercentageChange(overviewData.llmCalls.value, overviewData.llmCalls.change),
            changeType: (overviewData.llmCalls.change ?? 0) <= 0 ? "negative" as const : "positive" as const,
            changeDescription: `Previous month: ${(overviewData.llmCalls.value - (overviewData.llmCalls.change ?? 0)).toLocaleString()}`,
            textureImagePath: '/textures/llm-3.svg',
            chartData: overviewData.llmCalls.monthlyTrend.map(v => ({ value: v })),
            lastBarColor: (overviewData.llmCalls.change ?? 0) < 0 ? '#CA2B20' : '#03A65A',
        }
    ] : [];

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
        {isLoading ? (
            [...Array(3)].map((_, index) => (
                <Box
                    key={index}
                    sx={{
                        flexGrow: 1,
                        flexShrink: 0,
                        flexBasis: {
                            xs: '100%',
                            sm: `calc(50% - (${gap} / 2))`,
                            md: `calc(33.333% - (${gap} * 2 / 3))`
                        }
                    }}
                >
                    <SummaryCard
                        isLoading={true}
                        title=""
                        amount=""
                        textureImagePath=""
                    />
                </Box>
            ))
        ) : (
            cardItems.map((item) => (
                <Box
                    key={item.title}
                    sx={{
                        flexGrow: 1,
                        flexShrink: 0,
                        flexBasis: {
                            xs: '100%',
                            sm: `calc(50% - (${gap} / 2))`,
                            md: `calc(33.333% - (${gap} * 2 / 3))`
                        }
                    }}
                >
                    <SummaryCard
                        isLoading={false}
                        title={item.title}
                        amount={item.amount}
                        change={item.change}
                        changeType={item.changeType}
                        changeDescription={item.changeDescription}
                        textureImagePath={item.textureImagePath}
                        chartData={item.chartData}
                        lastBarColor={item.lastBarColor}
                    />
                </Box>
            ))
        )}
    </Box>
  );
};

export default LlmOverviewCards;

// import React from 'react';
// import { Box, useTheme } from '@mui/material';
// import SummaryCard from './SummaryCard';

// // Defines the structure of the data expected from the GraphQL query
// interface OverviewData {
//   totalTokens: { value: number; change: number | null };
//   totalCost: { value: number; change: number | null };
//   llmCalls: { value: number; change: number | null };
// }

// // Defines the component's props, accepting backend data and loading state
// interface LlmOverviewCardsProps {
//   isLoading?: boolean;
//   overviewData?: OverviewData;
// }

// // Helper function to calculate and format the percentage change.
// const formatPercentageChange = (current: number, change: number | null): string => {
//     if (change === null || change === 0) {
//         return '0.00%';
//     }
//     const previous = current - change;
//     if (previous === 0) {
//         return '100.00%';
//     }
//     const percentage = (change / Math.abs(previous)) * 100;
//     return `${Math.abs(percentage).toFixed(2)}%`;
// };

// // Helper function to generate plausible mock data for the mini-charts.
// const generatePlausibleChartData = (finalValue: number, points: number = 4): { value: number }[] => {
//     if (finalValue === 0) {
//         return Array(points).fill({ value: 0 });
//     }
//     const data = [];
//     for (let i = 0; i < points - 1; i++) {
//         const variance = (Math.random() - 0.5) * 0.5;
//         const pointValue = finalValue * (1 + variance);
//         data.push({ value: Math.max(0, Math.round(pointValue)) });
//     }
//     data.push({ value: finalValue });
//     return data;
// };


// const LlmOverviewCards: React.FC<LlmOverviewCardsProps> = ({ isLoading, overviewData }) => {
//     const theme = useTheme();
//     const gap = theme.spacing(3);

//     const cardItems = overviewData ? [
//         {
//             title: "Total Tokens",
//             amount: overviewData.totalTokens.value.toLocaleString(),
//             change: formatPercentageChange(overviewData.totalTokens.value, overviewData.totalTokens.change),
//             changeType: (overviewData.totalTokens.change ?? 0) <= 0 ? "negative" as const : "positive" as const,
//             // **CHANGE**: This text is now used ONLY for the tooltip.
//             changeDescription: `Previous month: ${(overviewData.totalTokens.value - (overviewData.totalTokens.change ?? 0)).toLocaleString()}`,
//             textureImagePath: '/textures/llm-1.svg',
//             chartData: generatePlausibleChartData(overviewData.totalTokens.value, 4),
//             lastBarColor: (overviewData.totalTokens.change ?? 0) < 0 ? '#CA2B20' : '#03A65A',
//         },
//         {
//             title: "Total Cost",
//             amount: `$${overviewData.totalCost.value.toFixed(2)}`,
//             change: formatPercentageChange(overviewData.totalCost.value, overviewData.totalCost.change),
//             changeType: (overviewData.totalCost.change ?? 0) <= 0 ? "negative" as const : "positive" as const,
//             // **CHANGE**: This text is now used ONLY for the tooltip.
//             changeDescription: `Previous month: $${(overviewData.totalCost.value - (overviewData.totalCost.change ?? 0)).toFixed(2)}`,
//             textureImagePath: '/textures/llm-2.svg',
//             chartData: generatePlausibleChartData(overviewData.totalCost.value, 4),
//             lastBarColor: (overviewData.totalCost.change ?? 0) < 0 ? '#CA2B20' : '#03A65A',
//         },
//         {
//             title: "LLM Calls",
//             amount: overviewData.llmCalls.value.toLocaleString(),
//             change: formatPercentageChange(overviewData.llmCalls.value, overviewData.llmCalls.change),
//             changeType: (overviewData.llmCalls.change ?? 0) <= 0 ? "negative" as const : "positive" as const,
//             // **CHANGE**: This text is now used ONLY for the tooltip.
//             changeDescription: `Previous month: ${(overviewData.llmCalls.value - (overviewData.llmCalls.change ?? 0)).toLocaleString()}`,
//             textureImagePath: '/textures/llm-3.svg',
//             chartData: generatePlausibleChartData(overviewData.llmCalls.value, 4),
//             lastBarColor: (overviewData.llmCalls.change ?? 0) < 0 ? '#CA2B20' : '#03A65A',
//         }
//     ] : [];

//   return (
//     <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
//         {isLoading ? (
//             [...Array(3)].map((_, index) => (
//                 <Box
//                     key={index}
//                     sx={{
//                         flexGrow: 1,
//                         flexShrink: 0,
//                         flexBasis: {
//                             xs: '100%',
//                             sm: `calc(50% - (${gap} / 2))`,
//                             md: `calc(33.333% - (${gap} * 2 / 3))`
//                         }
//                     }}
//                 >
//                     <SummaryCard
//                         isLoading={true}
//                         title=""
//                         amount=""
//                         textureImagePath=""
//                     />
//                 </Box>
//             ))
//         ) : (
//             cardItems.map((item) => (
//                 <Box
//                     key={item.title}
//                     sx={{
//                         flexGrow: 1,
//                         flexShrink: 0,
//                         flexBasis: {
//                             xs: '100%',
//                             sm: `calc(50% - (${gap} / 2))`,
//                             md: `calc(33.333% - (${gap} * 2 / 3))`
//                         }
//                     }}
//                 >
//                     <SummaryCard
//                         isLoading={false}
//                         title={item.title}
//                         amount={item.amount}
//                         change={item.change}
//                         changeType={item.changeType}
//                         changeDescription={item.changeDescription}
//                         textureImagePath={item.textureImagePath}
//                         chartData={item.chartData}
//                         lastBarColor={item.lastBarColor}
//                     />
//                 </Box>
//             ))
//         )}
//     </Box>
//   );
// };

// export default LlmOverviewCards;



